import React from 'react';
import { motion } from 'framer-motion';
import { Monitor, Zap, Users, Trophy, Target, BarChart3 } from 'lucide-react';

const Features = () => {
  const features = [
    {
      icon: Monitor,
      title: 'Dynamic Score Bugs',
      description: 'Real-time score overlays that update automatically with smooth animations and great designs.',
      color: 'from-blue-500 to-cyan-400'
    },
    {
      icon: Users,
      title: 'Team Lineups',
      description: 'Beautiful player lineup graphics with displays for any sport.',
      color: 'from-purple-500 to-pink-400'
    },
    {
      icon: Zap,
      title: 'Live Match Events',
      description: 'Instant goal alerts, substitutions, cards, and other match events with top quality animations.',
      color: 'from-green-500 to-emerald-400'
    },
    {
      icon: Trophy,
      title: 'Championship Graphics',
      description: 'Fixtures, standings, and championship graphics that elevate your broadcast quality.',
      color: 'from-yellow-500 to-orange-400'
    },
    {
      icon: Target,
      title: 'Custom Skins',
      description: 'Design your own skin templates or choose from our library of professional overlays.',
      color: 'from-red-500 to-pink-400'
    },
    {
      icon: BarChart3,
      title: 'Match Statistics',
      description: 'Customizable team statistics with all the key metrics that you can set.',
      color: 'from-indigo-500 to-purple-400'
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6
      }
    }
  };

  return (
    <section id="features" className="py-20 relative">
      {/* Background */}
      <div className="absolute inset-0 bg-slate-900/50"></div>
      
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div 
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Powerful Features for
            <span className="block bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">
              Professional Broadcasting
            </span>
          </h2>
          <p className="text-xl text-slate-300 max-w-3xl mx-auto">
            Everything you need to create stunning sports graphics that engage your audience and elevate your broadcast quality.
          </p>
        </motion.div>

        <motion.div 
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          {features.map((feature, index) => (
            <motion.div
              key={index}
              variants={itemVariants}
              className="group relative"
            >
              <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-8 h-full hover:bg-slate-700/50 transition-all duration-300 hover:border-slate-600/50 hover:shadow-xl">
                {/* Icon */}
                <div className={`inline-flex p-3 rounded-xl bg-gradient-to-r ${feature.color} mb-6 group-hover:scale-110 transition-transform duration-300`}>
                  <feature.icon className="h-6 w-6 text-white" />
                </div>

                {/* Content */}
                <h3 className="text-xl font-semibold text-white mb-4 group-hover:text-blue-400 transition-colors">
                  {feature.title}
                </h3>
                <p className="text-slate-300 leading-relaxed">
                  {feature.description}
                </p>

                {/* Hover Effect */}
                <div className="absolute inset-0 bg-gradient-to-r from-blue-500/5 to-cyan-500/5 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* Background Feature Image */}
        <motion.div 
          className="mt-20 relative"
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <div className="relative rounded-2xl overflow-hidden">
            <img 
              src="https://images.unsplash.com/photo-1616668010115-8f8ce69a4d04" 
              alt="Professional streaming setup"
              className="w-full h-96 object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 via-slate-900/20 to-transparent"></div>
            <div className="absolute inset-0 flex items-center justify-center">
              {/* <motion.button
                className="bg-white/20 backdrop-blur-sm border border-white/30 rounded-full p-6 hover:bg-white/30 transition-all duration-300"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
              >
                <Monitor className="h-8 w-8 text-white" />
              </motion.button> */}
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Features;